import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  Selection,

  SearchPanel,
  Export,
  Summary,
  TotalItem,
  GroupPanel
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import notify from 'devextreme/ui/notify';
import { Workbook } from "exceljs";
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

// [수정 1] user props 받기
export default function LotSummaryPage({ user }) {
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // [수정 2] 고객사 권한 여부 체크 (role이 'CUST'이면 고정)
  // user 객체가 없으면 false 처리
  const isCustFixed = user && user.role === 'CUST';

  // Master Grid 높이 상태
  const [masterHeight, setMasterHeight] = useState(400);

  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [dateTo, setDateTo] = useState(today);

  // [수정 3] 초기값 설정: CUST 권한이면 로그인 ID(username)를 바로 할당, 아니면 null
  const [custCd, setCustCd] = useState(isCustFixed ? user.username : null);
  
  const [deviCd, setDeviCd] = useState(null);
  const [operCd, setOperCd] = useState(null);
  const [lotNo, setLotNo] = useState("");

  const [custList, setCustList] = useState([]);
  const [deviList, setDeviList] = useState([]);
  const [operList, setOperList] = useState([]);

  const [masterData, setMasterData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [binHeaders, setBinHeaders] = useState([]);

  const getFmtDate = (d) => d ? new Date(d).toISOString().slice(0, 10).replace(/-/g, '') : '';
  const getDisplayDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

  // [수정 4] 날짜 변경 시 동작 수정
  useEffect(() => {
    // ★ 핵심: 고정된 사용자가 아닐 때만 null로 초기화
    if (!isCustFixed) {
      setCustCd(null);
    }
    
    // 하위 정보는 항상 초기화
    setDeviCd(null); 
    setOperCd(null);

    axios.get(`${API_URL}/api/oracle/combo/cust`, { 
      params: { dateFrom: getFmtDate(dateFrom), dateTo: getFmtDate(dateTo) } 
    }).then(res => {
      setCustList(res.data.data || []);
      
      // 혹시 목록 로드 후에도 고정값이 확실히 들어가도록 보장 (선택사항)
      if (isCustFixed) {
        setCustCd(user.username);
      }
    });
  }, [dateFrom, dateTo, isCustFixed, user]);

  useEffect(() => {
    setDeviCd(null); setOperCd(null);
    if (!custCd) return;
    axios.get(`${API_URL}/api/oracle/combo/device`, { 
      params: { dateFrom: getFmtDate(dateFrom), dateTo: getFmtDate(dateTo), custCd } 
    }).then(res => setDeviList(res.data.data || []));
  }, [custCd, dateFrom, dateTo]);

  useEffect(() => {
    setOperCd(null);
    if (!custCd || !deviCd) return;
    axios.get(`${API_URL}/api/oracle/combo/oper`, { 
      params: { dateFrom: getFmtDate(dateFrom), dateTo: getFmtDate(dateTo), custCd, deviCd } 
    }).then(res => setOperList(res.data.data || []));
  }, [deviCd, custCd, dateFrom, dateTo]);

  const handleSearch = useCallback(async () => {
    if (!custCd || !deviCd || !operCd) {
      notify("필수 항목을 모두 선택하세요.", "warning", 2000);
      return;
    }
    setLoading(true);
    try {
      const params = { custCd, deviCd, operCd, lotNo: lotNo.trim() || '%', dateFrom: getDisplayDate(dateFrom), dateTo: getDisplayDate(dateTo) };
      const [binRes, dataRes] = await Promise.all([
        axios.get(`${API_URL}/api/oracle/lot-summary/bin-desc`, { params }),
        axios.get(`${API_URL}/api/oracle/lot-summary/master`, { params })
      ]);
      setBinHeaders(binRes.data.data || []);
      setMasterData(dataRes.data.data || []);
    } catch (e) { notify(e.message, "error"); }
    finally { setLoading(false); }
  }, [custCd, deviCd, operCd, lotNo, dateFrom, dateTo]);

  const handleMasterClick = async (e) => {
    if (!e.data) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/oracle/lot-summary/detail`, { 
        params: { lotNo: e.data.lot_no, operCd: e.data.oper, dateFrom: getDisplayDate(dateFrom), dateTo: getDisplayDate(dateTo) } 
      });
      setDetailData(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const onExporting = (e, fileName) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
      });
    });
    e.cancel = true;
  };

  const renderBinColumns = useMemo(() => {
    return binHeaders.map((bin) => (
      <Column key={`b_${bin.seq}`} caption={bin.desc || `BIN ${bin.seq}`} isBand={true}
        headerCellRender={(d) => <div className="vertical-header"><span>{d.column.caption}</span></div>}>
        <Column dataField={`bin_${bin.seq}`} caption={bin.seq} dataType="number" format="#,##0" width={80} alignment="right"
          headerCellRender={(e) => <div className="bin-cell-header">{e.column.caption}</div>} />
      </Column>
    ));
  }, [binHeaders]);

  const handleSplitterMouseDown = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = masterHeight;

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientY - startY;
      const newHeight = startHeight + delta;
      if (newHeight > 150 && newHeight < window.innerHeight - 200) {
        setMasterHeight(newHeight);
      }
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div className="main-content lot-summary-page">
      <style>{`
        .vertical-header { height: 140px; display: flex; align-items: flex-end; justify-content: center; padding-bottom: 8px; }
        .vertical-header span { writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; font-size: 11px; font-weight: 600; }
        .bin-cell-header { font-size: 10px; text-align: center; font-weight: bold; }
        
        .splitter-horizontal {
            height: 10px;
            background: #f0f0f0;
            cursor: row-resize;
            display: flex;
            justify-content: center;
            align-items: center;
            border-top: 1px solid #ddd;
            border-bottom: 1px solid #ddd;
            transition: background 0.2s;
        }
        .splitter-horizontal:hover {
            background: #e0e0e0;
        }
        .splitter-handle {
            width: 40px;
            height: 4px;
            background: #aaa;
            border-radius: 2px;
        }
        .main-content {
            display: flex;
            flex-direction: column;
            height: 100vh;
            overflow: hidden;
        }
      `}</style>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">Searching...</div>
        </div>
      )}
      
      <div className="toolbar-container" style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px', borderBottom: '1px solid #ddd', flexShrink: 0, background: '#fff' }}>
        <DateBox value={dateFrom} onValueChanged={e => setDateFrom(e.value)} type="date" width={150} displayFormat="yyyy-MM-dd" />
        <DateBox value={dateTo} onValueChanged={e => setDateTo(e.value)} type="date" width={150} displayFormat="yyyy-MM-dd" />
        
        {/* [수정 5] CUST SelectBox: isCustFixed일 때 disabled 및 value 강제 */}
        <SelectBox 
            dataSource={custList} 
            value={custCd} 
            valueExpr="CODE_CD" 
            displayExpr="CODE_CD" 
            onValueChanged={e => setCustCd(e.value)} 
            width={180} 
            placeholder="CUST" 
            searchEnabled
            disabled={isCustFixed} // 고객사 권한이면 변경 불가
        />
        
        <SelectBox dataSource={deviList} value={deviCd} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setDeviCd(e.value)} width={200} placeholder="DEVICE" searchEnabled disabled={!custCd} />
        <SelectBox dataSource={operList} value={operCd} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setOperCd(e.value)} width={120} placeholder="OPER" disabled={!deviCd} />
        <TextBox value={lotNo} onValueChanged={e => setLotNo(e.value)} width={120} placeholder="LOT NO" />
        <Button text="SEARCH" icon="search" type="default" onClick={handleSearch} height={32} />
      </div>

      <div className="section-bar" style={{ flexShrink: 0 }}>Master Summary</div>
      
      <div style={{ height: `${masterHeight}px`, flexShrink: 0, transition: 'none' }}>
        <DataGrid 
          dataSource={masterData} 
          showBorders={true} 
          height="100%" 
          keyExpr="lot_no" 
          onRowClick={handleMasterClick} 
          allowColumnResizing={true} 
          rowAlternationEnabled={true}
          onExporting={(e) => onExporting(e, "MasterSummary")}
        >
          <Selection mode="single" />
       
          <SearchPanel visible={true} width={240} />
          <GroupPanel visible={true} />
          <Export enabled={true} />
          
          <Column caption="Lot Information" isBand={true} fixed={true}>
            <Column dataField="cust" caption="CUST" width={70} alignment="center" />
            <Column dataField="device" caption="DEVICE" width={120} />
            <Column dataField="lot_no" caption="LOT_NO" width={120} />
            <Column dataField="oper" caption="OPER" width={60} alignment="center" />
            <Column dataField="start_time" caption="START" width={130} alignment="center" />
            <Column dataField="end_time" caption="END" width={130} alignment="center" />
            <Column dataField="wafr_cnt" caption="QTY" width={80} alignment="center" />
            <Column dataField="total_die" caption="TOTAL" width={80} dataType="number" format="#,##0" />
            <Column dataField="good_die" caption="GOOD" width={80} dataType="number" format="#,##0" />
            <Column dataField="yield" caption="YIELD" width={70} dataType="number" format="#0.00" cellRender={e => <span style={{fontWeight:'bold', color: e.value < 90 ? 'red' : 'blue'}}>{e.value}%</span>} />
          </Column>
          {renderBinColumns}
          
          <Summary>
            <TotalItem column="lot_no" summaryType="count" displayFormat="Count: {0}" />
            <TotalItem column="total_die" summaryType="sum" displayFormat="{0}" />
            <TotalItem column="good_die" summaryType="sum" displayFormat="{0}" />
          </Summary>
        </DataGrid>
      </div>

      <div 
        className="splitter-horizontal" 
        onMouseDown={handleSplitterMouseDown}
        title="Drag to resize"
      >
        <div className="splitter-handle"></div>
      </div>

      <div className="section-bar" style={{ flexShrink: 0 }}>Wafer Details {detailLoading && "(Loading...)"}</div>
      
      <div style={{ flex: 1, minHeight: '100px', overflow: 'hidden' }}>
        <DataGrid 
          dataSource={detailData} 
          showBorders={true} 
          height="100%" 
          allowColumnResizing={true} 
          rowAlternationEnabled={true}
          onExporting={(e) => onExporting(e, "WaferDetail")}
        >
          <Export enabled={true} />
       
          <Column caption="Wafer Information" isBand={true} fixed={true}>
            <Column dataField="wafer_no" caption="WFR" width={60} alignment="center" />
            <Column dataField="start_time" caption="START" width={160} alignment="center" />
            <Column dataField="end_time" caption="END" width={160} alignment="center" />
            <Column dataField="total_die" caption="TOTAL" width={80} />
            <Column dataField="good_die" caption="GOOD" width={80} />
            <Column dataField="yield" caption="YIELD" width={70} cellRender={e => <span style={{fontWeight:'bold'}}>{e.value}%</span>} />
          </Column>
          {renderBinColumns}
          <Summary>
            <TotalItem column="wafer_no" summaryType="count" displayFormat="Cnt: {0}" />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
}